import { useTranslation } from 'react-i18next';
import { useRef, useEffect, useState } from 'react';
import calendarVideo from '../../assets/icons/animated/calendar.mp4';
import farmerVideo from '../../assets/icons/animated/Farmer.mp4';
import deliveryVideo from '../../assets/icons/animated/delivery.mp4';
import recycleVideo from '../../assets/icons/animated/recycle.mp4';

export const HowItWorksSection = () => {
  const { t } = useTranslation();
  const calendarVideoRef = useRef<HTMLVideoElement>(null);
  const farmerVideoRef = useRef<HTMLVideoElement>(null);
  const deliveryVideoRef = useRef<HTMLVideoElement>(null);
  const recycleVideoRef = useRef<HTMLVideoElement>(null);
  
  const [isMobile, setIsMobile] = useState(false);
  const [hoveredStep, setHoveredStep] = useState<string | null>(null);
  
  // Detect if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768 || 'ontouchstart' in window);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  const playVideo = (videoRef: React.RefObject<HTMLVideoElement>) => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {
        // Handle play() promise rejection silently
      });
    }
  };
  
  const pauseVideo = (videoRef: React.RefObject<HTMLVideoElement>) => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  const handleVideoInteraction = (icon: string) => {
    if (icon === 'calendar') playVideo(calendarVideoRef);
    else if (icon === 'farmer') playVideo(farmerVideoRef);
    else if (icon === 'delivery') playVideo(deliveryVideoRef);
    else if (icon === 'recycle') playVideo(recycleVideoRef);
  };

  const handleStepHover = (icon: string) => {
    if (!isMobile && hoveredStep !== icon) {
      setHoveredStep(icon);
      handleVideoInteraction(icon);
    }
  };

  const handleStepLeave = () => {
    if (!isMobile) {
      setHoveredStep(null);
    }
  };
  
  const steps = [
    {
      title: t('flowers.howItWorks.step1.title'),
      description: t('flowers.howItWorks.step1.description'),
      icon: 'calendar'
    },
    {
      title: t('flowers.howItWorks.step2.title'),
      description: t('flowers.howItWorks.step2.description'),
      icon: 'farmer'
    },
    {
      title: t('flowers.howItWorks.step3.title'),
      description: t('flowers.howItWorks.step3.description'),
      icon: 'delivery'
    },
    {
      title: t('flowers.howItWorks.step4.title'),
      description: t('flowers.howItWorks.step4.description'),
      icon: 'recycle'
    }
  ];
    
  return (
    <section 
      id="howItWorks-panel"
      role="region"
      aria-labelledby="howItWorks-heading" 
      tabIndex={0}
      aria-label="How Piika subscription works"
      className="py-12 px-6 max-w-md mx-auto bg-white border-b border-gray-100"
    >
      <h2 id="howItWorks-heading" className="text-2xl font-bold mb-1.5 text-center text-gray-900">
        {t('flowers.howItWorks.title')}
      </h2>
      
      <p className="text-center text-gray-600 mb-10 leading-relaxed">
        {t('flowers.howItWorks.description')}
      </p>
      
      <div className="space-y-10 flex flex-col items-center">
        {steps.map((item, index) => (
          <div 
            key={index} 
            className="flex items-start gap-5 px-2 cursor-pointer w-full"
            onClick={() => handleVideoInteraction(item.icon)}
            onTouchStart={() => handleVideoInteraction(item.icon)}
            onMouseEnter={() => handleStepHover(item.icon)}
            onMouseLeave={handleStepLeave}
          >
            <div className="flex-shrink-0">
              <div className="w-16 h-16 flex items-center justify-center text-2xl">
                {item.icon === 'calendar' ? (
                  <video 
                    ref={calendarVideoRef}
                    src={calendarVideo} 
                    muted
                    playsInline
                    preload="metadata"
                    className="w-16 h-16 object-cover pointer-events-none border-0 outline-none block"
                  />
                ) : item.icon === 'farmer' ? (
                  <video 
                    ref={farmerVideoRef}
                    src={farmerVideo} 
                    muted
                    playsInline
                    preload="metadata"
                    className="w-16 h-16 object-cover pointer-events-none border-0 outline-none block"
                  />
                ) : item.icon === 'delivery' ? (
                  <video 
                    ref={deliveryVideoRef}
                    src={deliveryVideo} 
                    muted
                    playsInline
                    preload="metadata"
                    className="w-16 h-16 object-cover pointer-events-none border-0 outline-none block"
                  />
                ) : item.icon === 'recycle' ? (
                  <video 
                    ref={recycleVideoRef}
                    src={recycleVideo} 
                    muted
                    playsInline
                    preload="metadata"
                    className="w-16 h-16 object-cover pointer-events-none border-0 outline-none block"
                  />
                ) : (
                  item.icon
                )}
              </div>
            </div>
            <div className="flex-1 pt-0">
              <h3 className="font-semibold text-base text-gray-800 mb-0.5">
                {item.title}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">
                {item.description}
              </p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}; 